import { openDB, type IDBPDatabase, type OpenDBCallbacks } from 'idb'
import type { StoredPhotoBook } from './photoBookSaver.types'
import {
  PhotoBookDbRetryError,
  PhotoBookDbTimeoutError,
} from './photoBookDbFailure'
import type { PhotoBookStorageBackend } from './photoBookStorageBackend.types'

const DB_NAME = 'mixtiles'
const PHOTO_BOOKS_OBJECT_STORE = 'photoBooks'
// An open that never settles would keep the editor's splash up for good.
const INDEXED_DB_LOAD_TIMEOUT_MS = 10_000

let dbPromise: Promise<IDBPDatabase> | null = null

type PhotoBookDbConnectionCallbacks = Pick<
  OpenDBCallbacks<unknown>,
  'terminated' | 'blocking'
>

// Some browsers hold a database without the store, which only a version bump
// can add. A bump waits on every open connection, and tabs on older releases
// never close theirs, so a database that has the store is opened as it is.
async function openDbWithPhotoBooksStore(
  connectionCallbacks: PhotoBookDbConnectionCallbacks
): Promise<IDBPDatabase> {
  const openDbAtVersion = (version: number | undefined) =>
    openDB(DB_NAME, version, {
      ...connectionCallbacks,
      upgrade(db) {
        if (!db.objectStoreNames.contains(PHOTO_BOOKS_OBJECT_STORE)) {
          db.createObjectStore(PHOTO_BOOKS_OBJECT_STORE, { keyPath: 'bookId' })
        }
      },
    })
  const db = await openDbAtVersion(undefined)
  if (db.objectStoreNames.contains(PHOTO_BOOKS_OBJECT_STORE)) {
    return db
  }
  db.close()
  return openDbAtVersion(db.version + 1)
}

const getDb = (): Promise<IDBPDatabase> => {
  if (dbPromise) {
    return dbPromise
  }
  const openingDbPromise: Promise<IDBPDatabase> = openDbWithPhotoBooksStore({
    // The browser closed the connection on us: the origin's storage was
    // cleared or evicted. Every later operation must open its own.
    terminated() {
      forgetDbPromise(openingDbPromise)
    },
    // Another tab opens a newer version, which waits until every older
    // connection closes. The next operation here reopens at this version.
    blocking() {
      forgetDbPromise(openingDbPromise)
      openingDbPromise.then((db) => db.close())
    },
  }).catch((err) => {
    // A cached rejection would answer every later call: a private-mode or
    // blocked open would take photo book storage down for the whole page.
    forgetDbPromise(openingDbPromise)
    throw err
  })
  dbPromise = openingDbPromise
  return dbPromise
}

// A killed connection's close event, or a second failure on it, can land after
// a retry has already cached a replacement. Never drop the replacement.
function forgetDbPromise(staleDbPromise: Promise<IDBPDatabase>): void {
  if (dbPromise === staleDbPromise) {
    dbPromise = null
  }
}

// The browser kills connections under a frozen tab, a page unload or cleared
// storage, and names the failure differently each time, so every failure gets
// one retry on a fresh connection. Every operation here is idempotent.
async function runWithOpenDb<T>(
  operation: (db: IDBPDatabase) => Promise<T>
): Promise<T> {
  try {
    return await runOnOpenDb(operation)
  } catch (err) {
    try {
      return await runOnOpenDb(operation)
    } catch (retryErr) {
      throw new PhotoBookDbRetryError(err, retryErr)
    }
  }
}

// A full disk or exceeded quota leaves the connection open, so a reopen
// without a close would leak one handle per failed operation.
async function runOnOpenDb<T>(
  operation: (db: IDBPDatabase) => Promise<T>
): Promise<T> {
  const openingDbPromise = getDb()
  const db = await openingDbPromise
  try {
    return await operation(db)
  } catch (err) {
    forgetDbPromise(openingDbPromise)
    db.close()
    throw err
  }
}

// Writes to one book run strictly in the order they were issued: a delete fired
// while a put is still in flight must land after it, or the deleted book comes
// back on the next load. A failed write must not wedge the chain, so the queued
// tail swallows the rejection while the caller still sees it.
const pendingWritesByBookId = new Map<string, Promise<unknown>>()

function enqueueBookWrite<T>(
  bookId: string,
  write: () => Promise<T>
): Promise<T> {
  const previousWrite = pendingWritesByBookId.get(bookId) ?? Promise.resolve()
  const writePromise = previousWrite.then(write)
  pendingWritesByBookId.set(
    bookId,
    writePromise.catch(() => undefined)
  )
  return writePromise
}

// The object store is the index: whatever it holds is the set of books this
// browser has. Records are parsed by the caller — this module only moves them.
const readAllStoredPhotoBookRecords = (): Promise<unknown[]> =>
  runWithOpenDb((db) => db.getAll(PHOTO_BOOKS_OBJECT_STORE))

// Takes a plain snapshot only: a MobX proxy fails structured cloning.
const putStoredPhotoBook = (book: StoredPhotoBook): Promise<void> =>
  enqueueBookWrite(book.bookId, () =>
    runWithOpenDb(async (db) => {
      await db.put(PHOTO_BOOKS_OBJECT_STORE, book)
    })
  )

const deleteStoredPhotoBook = (bookId: string): Promise<void> =>
  enqueueBookWrite(bookId, () =>
    runWithOpenDb((db) => db.delete(PHOTO_BOOKS_OBJECT_STORE, bookId))
  )

const readAllStoredPhotoBookRecordsWithinLoadTimeout = (): Promise<
  unknown[]
> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new PhotoBookDbTimeoutError(INDEXED_DB_LOAD_TIMEOUT_MS)),
      INDEXED_DB_LOAD_TIMEOUT_MS
    )
  })
  return Promise.race([
    readAllStoredPhotoBookRecords(),
    timeoutPromise,
  ]).finally(() => clearTimeout(timeoutId))
}

export const indexedDbPhotoBookBackend: PhotoBookStorageBackend = {
  name: 'indexed-db',
  readAllRecords: readAllStoredPhotoBookRecordsWithinLoadTimeout,
  putBook: putStoredPhotoBook,
  deleteBook: deleteStoredPhotoBook,
}
